import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle' | 'link';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg border border-accent hover:bg-accent-hover active:bg-accent-active shadow-xs',
  secondary:
    'bg-surface text-fg border border-border-strong hover:bg-surface-hover active:bg-surface-active shadow-xs',
  ghost: 'bg-transparent text-fg-muted border border-transparent hover:bg-surface-hover hover:text-fg',
  danger: 'bg-danger text-white border border-danger hover:bg-danger-hover shadow-xs',
  subtle: 'bg-accent-soft text-accent-text border border-transparent hover:bg-accent-soft-hover',
  link: 'bg-transparent text-accent-text border border-transparent underline-offset-4 hover:underline px-0',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-6 px-2 text-xs gap-1 rounded-sm',
  sm: 'h-7 px-2.5 text-sm gap-1.5 rounded-md',
  md: 'h-8 px-3 text-sm gap-2 rounded-md',
  lg: 'h-9 px-4 text-base gap-2 rounded-md',
};

const ICON_SIZES: Record<ButtonSize, string> = {
  xs: 'size-3',
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-4',
};

const ICON_ONLY: Record<ButtonSize, string> = {
  xs: 'w-6 px-0',
  sm: 'w-7 px-0',
  md: 'w-8 px-0',
  lg: 'w-9 px-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  isLoading?: boolean;
  /** Кнопка только с иконкой: обязателен aria-label. */
  iconOnly?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon: Icon,
    iconRight: IconRight,
    isLoading = false,
    iconOnly = false,
    fullWidth = false,
    className,
    disabled,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-colors duration-100 outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-canvas',
        'disabled:pointer-events-none disabled:opacity-50',
        SIZES[size],
        VARIANTS[variant],
        iconOnly && ICON_ONLY[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {isLoading ? (
        <Spinner size={size === 'lg' ? 'md' : 'xs'} className="text-current" />
      ) : Icon ? (
        <Icon className={cn(ICON_SIZES[size], 'shrink-0')} aria-hidden="true" />
      ) : null}
      {!iconOnly && children}
      {IconRight && !iconOnly ? (
        <IconRight className={cn(ICON_SIZES[size], 'shrink-0')} aria-hidden="true" />
      ) : null}
    </button>
  );
});

/** Группа кнопок с общей рамкой — переключатели режимов отображения. */
export function ButtonGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      role="group"
      className={cn(
        'inline-flex items-center rounded-md border border-border-strong bg-surface p-0.5 gap-0.5',
        className,
      )}
    >
      {children}
    </div>
  );
}
